import {
	ListAthletesExchangeProfile,
	SingleAthleteExchangeProfile,
	SingleExchangeAthleteRateCard,
} from '@/types/exchangeAthletes';
import { type ApiV3 } from '@teamworksdev/influencer-core';

import {
	getList,
	getSingle,
	isApiError,
	post,
	patch,
} from './utilities/provider';
import type { AxiosRequestConfig } from 'axios';

const RESOURCE = 'athletes';

export function useExchangeAthletesService(config?: Omit<AxiosRequestConfig, 'key'>) {
	const getExchangeAthletes = async (params?: ApiV3.GetAthletesRequestParams) => {
		const response = await getList<ApiV3.GetExchangeAthletesResponse>(RESOURCE, {
			key: 'getExchangeAthletes',
			params,
			...config,
		});
		if (isApiError(response)) throw response;
		const athletes = response.data.data.map((athlete) => new ListAthletesExchangeProfile(athlete));
		const mappedResponse = {
			...response.data,
			data: athletes,
		};
		return mappedResponse;
	};

	const getExchangeAthlete = async (id: string, params?: ApiV3.GetExchangeAthleteRequestParams) => {
		const response = await getSingle<ApiV3.GetSingleAthleteResponse>(RESOURCE, id, {
			key: 'getExchangeAthlete',
			params,
			...config,
		});
		if (isApiError(response)) throw response;
		return new SingleAthleteExchangeProfile(response.data.data);
	};

	const updateExchangeAthlete = async (id: string, data: ApiV3.UpdateExchangeAthleteRequestModel) => {
		const response = await patch<ApiV3.UpdateExchangeAthleteResponse>(RESOURCE, id, data, {
			key: 'updateExchangeAthlete',
			...config,
		});
		if (isApiError(response)) throw response;
		return response.data;
	};

	const getExchangeAthleteSocialStats = async (id: string) => {
		const response = await getSingle<ApiV3.GetExchangeAthleteSocialStatsResponse>(RESOURCE, `${id}/social-stats`, {
			key: 'getAthleteSocialStats',
			...config,
		});
		if (isApiError(response)) throw response;
		return response.data.data;
	};

	const getExchangeAthleteRates = async (uuid: string) => {
		const response = await getSingle<ApiV3.GetSingleAthleteRatesResponse>(RESOURCE, `${uuid}/rates`, {
			key: 'getExchangeAthleteRates',
			...config,
		});
		if (isApiError(response)) throw response;
		return response.data.data.map((rateCard) => new SingleExchangeAthleteRateCard(rateCard));
	};

	const getExchangeAthletePublicProfile = async (username: string) => {
		const response = await getSingle<ApiV3.GetSingleAthletePublicResponse>(`public/${RESOURCE}/${username}/external-profile`, '', {
			key: 'getExchangeAthletePublicProfile',
			requiresAuth: false,
			requiresAcceptedTou: false,
			requiresInflcrId: false,
			...config,
		});
		if (isApiError(response)) throw response;
		return new SingleAthleteExchangeProfile(response.data.data);
	};

	const exportAthletes = async () => {
		const response = await post<null>(`${RESOURCE}/export`, {
			key: 'exportAthletes',
			...config,
		});
		if (isApiError(response)) throw response;
		return response.status;
	};

	const getStudentAthleteScoreToken = async () => {
		const response = await getSingle<ApiV3.GetStudentAthleteScoreTokenResponse>(`${RESOURCE}/sa-score/token`, '', {
			key: 'getStudentAthleteScoreToken',
			...config,
		});
		if (isApiError(response)) throw response;
		return response.data;
	};

	const sendConfirmation = async (data: ApiV3.SendConfirmationRequestParams) => {
		const response = await post(`${RESOURCE}/send-confirmation`, data, {
			key: 'sendConfirmation',
			bypassAbort: true,
			...config,
		});
		if (isApiError(response)) throw response;
		return response.status;
	};

	return {
		getExchangeAthletes,
		getExchangeAthlete,
		updateExchangeAthlete,
		getExchangeAthleteSocialStats,
		getExchangeAthleteRates,
		getExchangeAthletePublicProfile,
		sendConfirmation,
		exportAthletes,
		getStudentAthleteScoreToken,
	};
}
